/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Text from '../../../shared/components/Text/Text';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import {
  deleteSavedCalendarItem,
  deleteSavedWorkoutItem,
  setDeleteStack,
  deleteSavedWorkoutItemsForWeek,
} from '../ducks/calendarActions';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
`;

const IconCircle = styled('div')`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #F5F5F5;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalText = styled('div')`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const WarningText = styled('div')`
  margin-top: 16px;
  padding: 12px;
  background: #FFF3CD;
  border: 1px solid #FFE69C;
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  text-align: left;
`;

const SubHeaderText = styled('div')`
  margin-bottom: 24px;
  text-align: left;
  width: 100%;
`;

const RadioOption = styled('label')`
  display: flex;
  align-items: center;
  padding: 12px 0;
  cursor: pointer;
  text-align: left;
  width: 100%;
`;

const RadioInput = styled('input')`
  width: 20px;
  height: 20px;
  margin-right: 12px;
  cursor: pointer;
`;

const RadioOptionsContainer = styled('div')`
  width: 100%;
  margin-bottom: 24px;
`;

const ButtonsContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DeleteButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: #3C3C3C;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #2C2C2C;
  }
`;

const CancelButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: white;
  color: #3C3C3C;
  border: 1px solid #D0D0D0;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #F5F5F5;
  }
`;

const CloseButton = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  cursor: pointer;
`;

const LinkText = styled('a')`
  color: #007AFF;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ConfirmDeleteModal = ({ closeModal }) => {
  const dispatch = useDispatch();
  const modalData = useSelector((state) => state.calendar.data.modalData);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const deleteStack = useSelector((state) => state.calendar.data.deleteStack);
  const programType = useSelector((state) => state.calendar.data.programType);
  const calendarItems = programType === 'free-form-program'
    ? useSelector((state) => state.calendar.data.nonDateBasedProgramItems) : useSelector((state) => state.calendar.data.calendarItems);
  const dateToUse = modalData?.date || selectedDate;
  const formattedDate = moment(dateToUse).format('YYYY-MM-DD');
  const itemCount = modalData?.bulkDeleteAssignedIds?.length || 1;
  const itemText = itemCount === 1 ? 'workout item' : 'workout items';
  const isRecurring = modalData?.isRecurring;

  const [deleteOption, setDeleteOption] = useState('single');

  const handleDelete = () => {
    if (modalData) {
      // Get the actual content to add to the delete stack
      const content = calendarItems[formattedDate]?.exercises[modalData?.itemIndex];
      if (content) {
        /** Add to delete stack */
        const newDeleteStack = [...deleteStack];
        newDeleteStack.push(content);
        dispatch(setDeleteStack(newDeleteStack));
      }

      /** we need to pass the id as an array for the endpoint because it also accomodates a bulk
       * delete it needed
       */
      const assignedIds = modalData?.isBulkDelete ? modalData?.bulkDeleteAssignedIds : [modalData?.assignedId];
      /** Delete the item */
      if (programType === 'free-form-program') {
        if (modalData.deleteWeek) {
          dispatch(deleteSavedWorkoutItemsForWeek(
            currentUser.accountCode,
            assignedIds,
            modalData.weekNumber,
          ));
        } else {
          dispatch(deleteSavedWorkoutItem(
            currentUser.accountCode,
            assignedIds,
            modalData?.itemIndex,
            modalData?.dayNumber,
          ));
        }
      } else {
        dispatch(deleteSavedCalendarItem(
          currentUser.accountCode,
          assignedIds,
          modalData?.itemIndex,
          formattedDate,
          isRecurring && deleteOption === 'all',
        ));
      }
    }
    closeModal();
  };

  return (
    <ModalContainer>
      <CloseButton onClick={closeModal}>
        <IcomoonReact
          iconSet={iconSet}
          size={15}
          icon='remove'
          color='grey'
        />
      </CloseButton>

      <IconCircle>
        <IcomoonReact
          iconSet={iconSet}
          size={32}
          icon='trashcan'
          color='#DB3030'
        />
      </IconCircle>

      <ModalText>
        {modalData?.programName && (
          <>
            <Text
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
              fontSize='20px'
              fontWeight={600}
            >
              {modalData?.programName}
            </Text>
            {modalData?.dayInWeek ? (
              <Text fontSize='18px'>{`Week ${modalData?.weekNumber} - Day ${modalData?.dayInWeek}`}</Text>
            ) : (
              <Text fontSize='18px'>{`Week ${modalData?.weekNumber}`}</Text>
            )}
          </>
        )}
        <Text fontSize='20px' fontWeight={600}>
          {isRecurring ? 'Delete Recurring Component' : `You are about to delete (${itemCount}) ${itemText}`}
        </Text>
      </ModalText>

      {isRecurring && (
        <SubHeaderText>
          <Text fontSize='14px' color='#666666'>
            All changes made to a recurring item will be reflected on every day that it is scheduled (including past instances). <LinkText href='#'>Click here to learn more.</LinkText>
          </Text>
        </SubHeaderText>
      )}

      {isRecurring && (
        <RadioOptionsContainer>
          <RadioOption>
            <RadioInput
              type='radio'
              name='deleteOption'
              value='single'
              checked={deleteOption === 'single'}
              onChange={(e) => setDeleteOption(e.target.value)}
            />
            <Text fontSize='16px'>Only this occurrence</Text>
          </RadioOption>
          <RadioOption>
            <RadioInput
              type='radio'
              name='deleteOption'
              value='all'
              checked={deleteOption === 'all'}
              onChange={(e) => setDeleteOption(e.target.value)}
            />
            <Text fontSize='16px'>All occurrences</Text>
          </RadioOption>
        </RadioOptionsContainer>
      )}

      {modalData?.isParentCalendarItem && (
        <WarningText>
          <IcomoonReact
            iconSet={iconSet}
            size={16}
            icon='warning'
            color='#856404'
            style={{ marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
          />
          <Text fontSize='14px' color='#856404'>
            Warning: you are trying to delete an item that belongs to a parent calendar - this action will only affect the item on the child calendar
          </Text>
        </WarningText>
      )}

      <ButtonsContainer>
        <DeleteButton onClick={handleDelete}>
          DELETE
        </DeleteButton>
        <CancelButton onClick={closeModal}>
          CANCEL
        </CancelButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

ConfirmDeleteModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default ConfirmDeleteModal;
